/** @jsxRuntime classic */

/** @jsx jsx */
import { jsx } from "@emotion/react";
import { LoaderLayout } from "components/atoms/loader/LoaderLayout";
import { TICKET_CLASS } from "constants/ticketClass";
import { AccountReceipt } from "containers/account/receipt/AccountReceipt";
import { useI18n } from "hooks/i18n/i18n";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { SubContentsLayout } from "ui/SubContentsLayout";

import { OrderInfo } from "./components/OrderInfo";
import { ProductActionButtons } from "./components/ProductActionButtons";
import { ProductInfo } from "./components/ProductInfo";
import { PRODUCT_TYPE } from "./constants/ProductType";
import {
  containerStyle,
  contentWrapperStyle,
  informationStyle,
  titleStyle,
} from "./styles";

import type { DeliveryData } from "./types/DeliveryData";
import type { FulfillmentData } from "./types/FulfillmentData";
import type { OrderData } from "./types/OrderData";
import type { ProductItem } from "./types/ProductItem";
import type { ReceiptData } from "containers/account/receipt/AccountReceipt";
import type { FC, ReactNode } from "react";

type Props = {
  deliveryData: DeliveryData | null;
  fulfillmentData: FulfillmentData | null;
  handleCloseReceipt: () => void;
  handleContinuePayment: () => void;
  handleCopyOrderId: (orderId: string) => void;
  handleShowReceipt: () => void;
  isDisplayReceipt: boolean;
  isFetchError: boolean;
  isLoadingAddress: boolean;
  isLoadingBonus: boolean;
  isLoadingFulfillment: boolean;
  isPayPalPending: boolean;
  isPurchased: boolean;
  isReceiptAvailable: boolean;
  orderData: OrderData | null;
  orderList: ProductItem[];
  receiptData: ReceiptData | null;
  renderConfirmCvsPayment: () => ReactNode;
  showPaymentAlert: boolean;
};

export const View: FC<Props> = (props) => {
  const {
    deliveryData,
    fulfillmentData,
    handleCloseReceipt,
    handleContinuePayment,
    handleCopyOrderId,
    handleShowReceipt,
    isDisplayReceipt,
    isFetchError,
    isLoadingAddress,
    isLoadingBonus,
    isLoadingFulfillment,
    isPayPalPending,
    isPurchased,
    isReceiptAvailable,
    orderData,
    orderList,
    receiptData,
    renderConfirmCvsPayment,
    showPaymentAlert,
  } = props;

  const { t } = useI18n();

  if (isDisplayReceipt && receiptData !== null) {
    return (
      <AccountReceipt
        receiptData={receiptData}
        resetState={handleCloseReceipt}
      />
    );
  }

  if (isFetchError) {
    return <div id="contents-layout">{t("error.unknown")}</div>;
  }

  if (!orderData) {
    return <LoaderLayout />;
  }

  const isLiveStream = (item: ProductItem) => {
    if (item.type !== PRODUCT_TYPE.TICKET) return false;
    return (
      item.liveTicketInfo.class === TICKET_CLASS.LIVE_STREAM ||
      item.liveTicketInfo.class === TICKET_CLASS.VOD_STREAM
    );
  };

  return (
    <SubContentsLayout bgcolor="GRAY">
      {renderConfirmCvsPayment()}
      <BreadcrumbNavigation
        breadcrumbs={[
          { label: t("common.routes.home"), link: "/" },
          {
            label: t("common.routes.settlementHistory"),
            link: "/account/settlement/history",
          },
          { label: t("settlementHistory.breadcrumb.orderDetails"), link: null },
        ]}
      />
      <div css={containerStyle}>
        <div css={contentWrapperStyle}>
          <h1 css={titleStyle}>{t("settlementHistory.pageTitle")}</h1>
          <div css={informationStyle}>
            <OrderInfo
              deliveryData={deliveryData ?? undefined}
              fulfillmentData={fulfillmentData ?? undefined}
              isLoadingAddress={isLoadingAddress}
              isLoadingFulfillment={isLoadingFulfillment}
              isPayPalPending={isPayPalPending}
              isPurchased={isPurchased}
              isReceiptAvailable={isReceiptAvailable}
              onContinuePayment={handleContinuePayment}
              onCopyOrderId={handleCopyOrderId}
              onShowReceipt={handleShowReceipt}
              orderData={orderData}
              showPaymentAlert={showPaymentAlert}
            />
            {orderList.map((item) => (
              <div key={`${item.productName}-${item.variationName}`}>
                <ProductInfo {...item} />
                {item.type === PRODUCT_TYPE.TICKET && (
                  <ProductActionButtons
                    eventId={item.liveTicketInfo.eventId}
                    hasBonusMedia={item.liveTicketInfo.hasBonusMedia}
                    isLiveStreaming={isLiveStream(item)}
                    isLoadingBonus={isLoadingBonus}
                    isPurchased={isPurchased}
                    orderId={orderData.orderId}
                    ticketId={item.liveTicketInfo.ticketId}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </SubContentsLayout>
  );
};
